import { useState, useRef, useCallback } from 'react';
import { Image as ImageIcon, Upload, Copy, Check, Sparkles, ChevronDown } from 'lucide-react';
import { imageService } from '../../../services/imageService';
import type { ImageFolder } from '../../../types/images';
import ImageInsertDialog from '../../../pickers/images/ImageInsertDialog';
import Button from '../../../components/Button';

interface MarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    folder: ImageFolder;
    className?: string;
    rows?: number;
}

export default function MarkdownEditor({
    value,
    onChange,
    placeholder = "# Page Title\n\nYour markdown content here...",
    folder,
    className = "",
    rows = 30
}: MarkdownEditorProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showPlaceholderMenu, setShowPlaceholderMenu] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const placeholderMenuRef = useRef<HTMLDivElement>(null);

    const insertImageAtCursor = useCallback((imageUrl: string, altText: string) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            // Fallback: append to end
            const imageMarkdown = `\n\n![${altText}](${imageUrl})\n\n`;
            onChange(value + imageMarkdown);
            return;
        }

        // Insert at cursor position
        const cursorPosition = textarea.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);
        const textAfterCursor = value.substring(cursorPosition);

        // Add appropriate spacing
        const needsNewlineBefore = textBeforeCursor.length > 0 && !textBeforeCursor.endsWith('\n');
        const needsNewlineAfter = textAfterCursor.length > 0 && !textAfterCursor.startsWith('\n');

        const beforeSpacing = needsNewlineBefore ? '\n\n' : '\n';
        const afterSpacing = needsNewlineAfter ? '\n\n' : '\n';

        const imageMarkdown = `${beforeSpacing}![${altText}](${imageUrl})${afterSpacing}`;
        const newContent = textBeforeCursor + imageMarkdown + textAfterCursor;

        onChange(newContent);

        // Move cursor after inserted image
        setTimeout(() => {
            const newCursorPosition = cursorPosition + imageMarkdown.length;
            textarea.setSelectionRange(newCursorPosition, newCursorPosition);
            textarea.focus();
        }, 0);
    }, [value, onChange]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));

        if (imageFiles.length === 0) return;

        for (const file of imageFiles) {
            // Validate file
            const validation = imageService.validateFile(file);
            if (!validation.isValid) {
                console.error('Invalid file:', validation.error);
                continue;
            }

            try {
                setUploadingImage(true);

                // Upload image
                const imageUrl = await imageService.uploadImage(file, folder);

                // Generate alt text from filename
                const altText = file.name.split('.')[0]
                    .replace(/[-_]/g, ' ')
                    .replace(/^\d+-/, '') // Remove timestamp prefix
                    .trim() || 'Uploaded image';

                // Insert image at cursor position
                insertImageAtCursor(imageUrl, altText);

            } catch (error) {
                console.error('Failed to upload image:', error);
            } finally {
                setUploadingImage(false);
            }
        }
    }, [folder, insertImageAtCursor]);

    const handleImageInsert = (imageUrl: string, altText: string) => {
        insertImageAtCursor(imageUrl, altText);
        setShowImageDialog(false);
    };

    const insertTextAtCursor = useCallback((text: string) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            onChange(value + text);
            return;
        }

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = value.substring(0, start) + text + value.substring(end);
        onChange(newContent);

        setTimeout(() => {
            const newPos = start + text.length;
            textarea.setSelectionRange(newPos, newPos);
            textarea.focus();
        }, 0);
    }, [value, onChange]);

    const handleInsertAiBlock = useCallback(() => {
        insertTextAtCursor('{{ai:Describe the personalization goal here|Your page content with [[text to personalize]] here|15-40}}');
        setShowPlaceholderMenu(false);
    }, [insertTextAtCursor]);

    const handleWrapSelectionWithZone = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = value.substring(start, end);

        if (!selectedText) return;

        const newContent = value.substring(0, start) + '[[' + selectedText + ']]' + value.substring(end);
        onChange(newContent);

        setTimeout(() => {
            const newEnd = end + 4;
            textarea.setSelectionRange(newEnd, newEnd);
            textarea.focus();
        }, 0);

        setShowPlaceholderMenu(false);
    }, [value, onChange]);

    const handleCopyToClipboard = useCallback(async () => {
        try {
            // Remove code blocks (badge parts) that start with ```
            const contentWithoutBadges = value.replace(/```[\s\S]*?```/g, '').trim();
            await navigator.clipboard.writeText(contentWithoutBadges);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    }, [value]);

    return (
        <div className={`relative ${className}`}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2 p-2 bg-gray-50 border border-gray-200 rounded-t-md">
                <label className="text-sm font-medium text-gray-700">
                    Content (Markdown)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={handleCopyToClipboard}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                        title="Copy to clipboard"
                    >
                        {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                    <div className="sm:relative" ref={placeholderMenuRef}>
                        <Button
                            type="button"
                            onClick={() => setShowPlaceholderMenu(!showPlaceholderMenu)}
                            variant="outline"
                            size="sm"
                            icon={<Sparkles size={14} />}
                            title="Insert personalization placeholder"
                        >
                            Placeholder <ChevronDown size={12} />
                        </Button>
                        {showPlaceholderMenu && (
                            <div className="absolute right-0 mt-1 w-full sm:w-96 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                                <div className="border-b border-gray-200">
                                    <div className="px-4 py-2 bg-blue-50 text-xs font-semibold text-blue-700 uppercase tracking-wide">
                                        AI Personalization
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleInsertAiBlock}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-sm"
                                    >
                                        <span className="font-mono text-blue-700 break-words">{`{{ai:instruction|template with [[zone]]|min-max}}`}</span>
                                        <span className="block text-xs text-gray-500 mt-0.5">Wrap the entire page in an AI block. The [[zone]] is what gets personalized.</span>
                                    </button>
                                </div>
                                <div>
                                    <div className="px-4 py-2 bg-purple-50 text-xs font-semibold text-purple-700 uppercase tracking-wide">
                                        Mark Zone
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleWrapSelectionWithZone}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-sm"
                                    >
                                        <span className="font-mono text-purple-700">{'[[selected text]]'}</span>
                                        <span className="block text-xs text-gray-500 mt-0.5">Wrap the selected text in [[brackets]] to mark it for AI personalization</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <Button
                        type="button"
                        onClick={() => setShowImageDialog(true)}
                        variant="outline"
                        size="sm"
                        icon={<ImageIcon size={14} />}
                        title="Insert image from library"
                    >
                        Insert Image
                    </Button>
                </div>
            </div>

            {/* Editor */}
            <div className="relative">
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none ${isDragging ? 'border-blue-500 bg-blue-50' : ''
                        }`}
                    style={{ height: `${rows * 1.5}rem` }}
                    placeholder={placeholder}
                />

                {/* Drag Overlay */}
                {isDragging && (
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-10 border-2 border-dashed border-blue-500 rounded-md flex items-center justify-center pointer-events-none">
                        <div className="text-center text-blue-600">
                            <Upload size={48} className="mx-auto mb-2" />
                            <p className="text-lg font-medium">Drop images here to upload</p>
                            <p className="text-sm">Images will be uploaded to {folder} folder</p>
                        </div>
                    </div>
                )}

                {/* Upload Indicator */}
                {uploadingImage && (
                    <div className="absolute top-4 right-4 bg-white border border-gray-200 rounded-md px-3 py-2 shadow-md">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                            Uploading image...
                        </div>
                    </div>
                )}
            </div>

            {/* Image Insert Dialog */}
            <ImageInsertDialog
                isOpen={showImageDialog}
                onClose={() => setShowImageDialog(false)}
                onInsert={handleImageInsert}
                folder={folder}
                title="Insert Image into Content"
            />
        </div>
    );
} 